import { devLog, devError } from '../utils/devLogger';
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Form, Alert, Spinner, Table, Badge } from 'react-bootstrap';
import { useAuth } from '../auth/AuthContext';
import { db } from '../firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';
import AdminNavbar from '../components/AdminNavbar';
import BEE123ExportDashboard from '../components/BEE123ExportDashboard';
import '../styles/brand.css';

const BEEExport = () => {
  const { currentUser, userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [learnerData, setLearnerData] = useState([]);
  const [exportStats, setExportStats] = useState({
    totalLearners: 0,
    completedLearners: 0,
    inProgressLearners: 0,
    exportReady: 0
  });
  const [alert, setAlert] = useState({ show: false, message: '', type: '' });
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 90 days ago
    endDate: new Date().toISOString().split('T')[0] // today
  });

  useEffect(() => {
    if (currentUser && userProfile?.role === 'admin') {
      loadBEEData();
    }
  }, [currentUser, userProfile, dateRange]);

  const loadBEEData = async () => {
    try {
      setLoading(true);
      
      // Load users (learners)
      const usersCollection = collection(db, 'users');
      const usersQuery = query(usersCollection, where('role', '==', 'learner'));
      const usersSnapshot = await getDocs(usersQuery);
      const users = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        registrationDate: doc.data().createdAt?.toDate?.() || new Date(doc.data().createdAt || Date.now())
      }));
      
      // Load enrollments
      const enrollmentsCollection = collection(db, 'enrollments');
      const enrollmentsSnapshot = await getDocs(enrollmentsCollection);
      const enrollments = enrollmentsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        enrolledAt: doc.data().createdAt?.toDate?.() || new Date(doc.data().createdAt || Date.now()),
        completedAt: doc.data().completedAt?.toDate?.() || null
      }));
      
      // Load modules for training details
      const modulesCollection = collection(db, 'modules');
      const modulesSnapshot = await getDocs(modulesCollection);
      const modules = modulesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Process data for BEE reporting
      const startDate = new Date(dateRange.startDate);
      const endDate = new Date(dateRange.endDate);
      
      const processedData = users.map(user => {
        const userEnrollments = enrollments.filter(e => e.userId === user.id);
        const completedEnrollments = userEnrollments.filter(e => e.completed && e.completedAt >= startDate && e.completedAt <= endDate);
        
        // Calculate training hours
        const totalTrainingHours = completedEnrollments.reduce((total, enrollment) => {
          const module = modules.find(m => m.id === enrollment.moduleId);
          return total + (module?.estimatedHours || 0);
        }, 0);
        
        return {
          ...user,
          enrollments: userEnrollments.length,
          completedCourses: completedEnrollments.length,
          totalTrainingHours,
          lastActivity: userEnrollments.length > 0 ? 
            Math.max(...userEnrollments.map(e => e.lastAccessedAt?.toDate?.() || e.enrolledAt)) : null,
          beeCompliant: completedEnrollments.length > 0 && totalTrainingHours >= 8, // Minimum 8 hours for BEE compliance
          skillsPrograms: completedEnrollments.map(e => {
            const module = modules.find(m => m.id === e.moduleId);
            return {
              name: module?.title || 'Unknown',
              completionDate: e.completedAt,
              hours: module?.estimatedHours || 0,
              grade: e.finalScore || 0
            };
          })
        };
      });
      
      // Filter by date range
      const filteredData = processedData.filter(user => {
        if (user.skillsPrograms.length === 0) return false;
        return user.skillsPrograms.some(program => 
          program.completionDate >= startDate && program.completionDate <= endDate
        );
      });
      
      setLearnerData(filteredData);
      
      // Calculate statistics
      setExportStats({
        totalLearners: filteredData.length,
        completedLearners: filteredData.filter(u => u.completedCourses > 0).length,
        inProgressLearners: filteredData.filter(u => u.enrollments > u.completedCourses).length,
        exportReady: filteredData.filter(u => u.beeCompliant).length
      });
      
    } catch (error) {
      devError('Error loading BEE data:', error);
      showAlert(`Error loading BEE export data: ${error.message || 'Unknown error'}`, 'danger');
    } finally {
      setLoading(false);
    }
  };

  const showAlert = (message, type) => {
    setAlert({ show: true, message, type });
    setTimeout(() => setAlert({ show: false, message: '', type: '' }), 5000);
  };

  const handleDateChange = (field, value) => {
    setDateRange(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const exportToCSV = () => {
    setExporting(true);
    
    try {
      // CSV Headers
      const headers = [
        'ID Number',
        'First Name',
        'Last Name',
        'Email',
        'Phone',
        'Province',
        'Race',
        'Gender',
        'Disability',
        'Employment Status',
        'Training Program',
        'Start Date',
        'Completion Date',
        'Training Hours',
        'Grade/Score',
        'Certificate Status',
        'Skills Development Type'
      ];
      
      // CSV Data
      const csvData = [];
      csvData.push(headers.join(','));
      
      learnerData.forEach(learner => {
        if (learner.skillsPrograms.length > 0) {
          learner.skillsPrograms.forEach(program => {
            const row = [
              learner.idNumber || '',
              learner.firstName || '',
              learner.lastName || '',
              learner.email || '',
              learner.phoneNumber || '',
              learner.province || '',
              learner.race || '',
              learner.gender || '',
              learner.disability || 'No',
              learner.employmentStatus || '',
              program.name,
              learner.registrationDate.toISOString().split('T')[0],
              program.completionDate.toISOString().split('T')[0],
              program.hours,
              program.grade,
              program.grade >= 60 ? 'Competent' : 'Not Yet Competent',
              'Skills Programme'
            ];
            csvData.push(row.join(','));
          });
        }
      });
      
      // Create and download CSV file
      const csvContent = csvData.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `BEE_Export_${dateRange.startDate}_to_${dateRange.endDate}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      
      showAlert('BEE export file downloaded successfully!', 'success');
      
    } catch (error) {
      devError('Error exporting CSV:', error);
      showAlert('Error creating export file', 'danger');
    } finally {
      setExporting(false);
    }
  };

  if (userProfile?.role !== 'admin') {
    return (
      <Container className="mt-5">
        <Alert variant="danger">
          <h4>Access Denied</h4>
          <p>You do not have permission to access BEE exports.</p>
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <>
        <AdminNavbar currentPage="bee-export" />
        <Container className="mt-4">
          <div className="text-center my-5">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <p className="mt-2">Loading BEE export data...</p>
          </div>
        </Container>
      </>
    );
  }

  return (
    <>
      <AdminNavbar currentPage="bee-export" />

      {/* Hero Section */}
      <section className="hero-section">
        <Container>
          <Row>
            <Col lg={8}>
              <h1 className="mb-3">BEE Export Dashboard</h1>
              <p className="mission-text mb-4">
                Generate BEE compliance reports and export learner data
              </p>
              <p className="mb-0">
                Create comprehensive BEE compliance reports with learner demographics,
                skills programme completions, and certification data for SETA submissions.
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      <Container className="mt-4">

        {alert.show && (
          <Alert variant={alert.type} className="mb-4">
            {alert.message}
          </Alert>
        )}

        {/* Date Range Selection */}
        <Row className="mb-4">
          <Col md={6}>
            <Card>
              <Card.Header>
                <h5>Report Period</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Start Date</Form.Label>
                      <Form.Control
                        type="date"
                        value={dateRange.startDate}
                        onChange={(e) => handleDateChange('startDate', e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>End Date</Form.Label>
                      <Form.Control
                        type="date"
                        value={dateRange.endDate}
                        onChange={(e) => handleDateChange('endDate', e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Button 
                  variant="primary" 
                  onClick={exportToCSV}
                  disabled={exporting || learnerData.length === 0}
                >
                  {exporting ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Exporting...
                    </>
                  ) : (
                    <>Export BEE CSV</>
                  )}
                </Button>
              </Card.Body>
            </Card>
          </Col>
          <Col md={6}>
            <Card>
              <Card.Header>
                <h5>Export Statistics</h5>
              </Card.Header>
              <Card.Body>
                <Row className="text-center">
                  <Col md={6} className="mb-3">
                    <div>
                      <h4 className="text-primary">{exportStats.totalLearners}</h4>
                      <small className="text-muted">Total Learners</small>
                    </div>
                  </Col>
                  <Col md={6} className="mb-3">
                    <div>
                      <h4 className="text-success">{exportStats.exportReady}</h4>
                      <small className="text-muted">BEE Compliant</small>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* BEE123 Export Dashboard Component */}
        <BEE123ExportDashboard />

        {/* Export Preview Table */}
        <Card className="mt-4">
          <Card.Header>
            <div className="d-flex justify-content-between align-items-center">
              <h5>Export Preview</h5>
              <Badge bg="info">{learnerData.length} records</Badge>
            </div>
          </Card.Header>
          <Card.Body>
            <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
              <Table striped bordered hover size="sm">
                <thead>
                  <tr>
                    <th>Learner</th>
                    <th>ID Number</th>
                    <th>Province</th>
                    <th>Completed Programs</th>
                    <th>Training Hours</th>
                    <th>BEE Compliant</th>
                    <th>Last Activity</th>
                  </tr>
                </thead>
                <tbody>
                  {learnerData.map((learner) => (
                    <tr key={learner.id}>
                      <td>
                        <div>
                          <strong>{learner.displayName}</strong>
                          <div><small className="text-muted">{learner.email}</small></div>
                        </div>
                      </td>
                      <td>{learner.idNumber || 'N/A'}</td>
                      <td>{learner.province || 'N/A'}</td>
                      <td>
                        <Badge bg="primary">{learner.completedCourses}</Badge>
                      </td>
                      <td>
                        <Badge bg={learner.totalTrainingHours >= 8 ? 'success' : 'warning'}>
                          {learner.totalTrainingHours}h
                        </Badge>
                      </td>
                      <td>
                        {learner.beeCompliant ? (
                          <Badge bg="success">Compliant</Badge>
                        ) : (
                          <Badge bg="warning">Pending</Badge>
                        )}
                      </td>
                      <td>
                        {learner.lastActivity ? (
                          <small>{new Date(learner.lastActivity).toLocaleDateString()}</small>
                        ) : (
                          <small className="text-muted">No activity</small>
                        )}
                      </td>
                    </tr>
                  ))}
                  {learnerData.length === 0 && (
                    <tr>
                      <td colSpan="7" className="text-center text-muted py-4">
                        No learner data available for the selected date range.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>

        {/* Export Instructions */}
        <Card className="mt-4">
          <Card.Header>
            <h5>BEE Export Information</h5>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <h6>What's Included in the Export:</h6>
                <ul>
                  <li>Learner demographic information</li>
                  <li>Skills programme completion records</li>
                  <li>Training hours and certificates</li>
                  <li>BEE compliance indicators</li>
                  <li>Assessment results and grades</li>
                </ul>
              </Col>
              <Col md={6}>
                <h6>BEE Compliance Criteria:</h6>
                <ul>
                  <li>Minimum 8 hours of training</li>
                  <li>Successfully completed skills programme</li>
                  <li>Valid completion certificate</li>
                  <li>Demographic data captured</li>
                  <li>Assessment score ≥ 60%</li>
                </ul>
              </Col>
            </Row>
            <Alert variant="info" className="mt-3">
              <strong>Note:</strong> This export is formatted for BEE compliance reporting and includes all required fields as per South African BEE scorecards and SETA requirements.
            </Alert>
          </Card.Body>
        </Card>
      </Container>
    </>
  );
};

export default BEEExport;
